import React, { useState, useEffect } from 'react';
import axios from 'axios';

const CountryPhoneInput = ({ 
  phoneValue, 
  countryValue, 
  onPhoneChange, 
  onCountryChange, 
  disabled = false, 
  required = false,
  placeholder = "Enter phone number"
}) => {
  const [countries, setCountries] = useState([]);
  const [popularCountries, setPopularCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const [countriesResponse, popularResponse] = await Promise.all([
        axios.get('/api/countries'),
        axios.get('/api/countries/popular')
      ]);
      
      setCountries(countriesResponse.data);
      setPopularCountries(popularResponse.data);
      
      // Set default to India if no country selected
      if (!countryValue && popularResponse.data.length > 0) {
        const india = popularResponse.data.find(c => c.countryCode === '91');
        if (india) {
          onCountryChange(india.id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSelectedCountry = () => {
    return countries.find(c => c.id === countryValue) || 
           popularCountries.find(c => c.id === countryValue);
  };

  const filteredCountries = countries.filter(country =>
    country.countryName.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.countryCode.includes(countrySearch)
  );

  const selectedCountry = getSelectedCountry();

  return (
    <div style={{ display: 'flex', gap: '0' }}>
      {/* Country Code Selector */}
      <div style={{ position: 'relative', width: '140px' }}>
        <button
          type="button"
          onClick={() => setShowCountryDropdown(!showCountryDropdown)}
          disabled={disabled || loading}
          style={{
            width: '100%',
            height: '42px',
            padding: '10px 8px',
            border: '2px solid var(--gray-medium)',
            borderRight: '1px solid var(--gray-medium)',
            borderRadius: '6px 0 0 6px',
            background: disabled ? 'var(--gray-light)' : 'var(--white)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '14px',
            color: 'var(--gray-dark)'
          }}
        >
          <span>
            {loading ? '...' : (selectedCountry ? `+${selectedCountry.countryCode}` : '+91')}
          </span>
          <span style={{ fontSize: '10px' }}>▼</span>
        </button>

        {showCountryDropdown && !disabled && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '0',
            right: '0',
            background: 'var(--white)',
            border: '2px solid var(--primary-blue)',
            borderRadius: '6px',
            boxShadow: '0 4px 15px rgba(74, 144, 226, 0.2)',
            zIndex: 1000,
            maxHeight: '300px',
            overflowY: 'auto',
            marginTop: '2px'
          }}>
            {/* Search input */}
            <div style={{ padding: '8px', borderBottom: '1px solid var(--gray-medium)' }}>
              <input
                type="text"
                placeholder="Search countries..."
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                style={{
                  width: '100%',
                  padding: '6px 8px',
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '4px',
                  fontSize: '12px'
                }}
                autoFocus
              />
            </div>

            {/* Popular countries section */}
            {!countrySearch && popularCountries.length > 0 && (
              <div>
                <div style={{
                  padding: '8px 12px',
                  background: 'var(--pale-blue)',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: 'var(--dark-blue)'
                }}>
                  Popular Countries
                </div>
                {popularCountries.map((country) => (
                  <div
                    key={country.id}
                    onClick={() => {
                      onCountryChange(country.id);
                      setShowCountryDropdown(false);
                      setCountrySearch('');
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid var(--gray-light)',
                      fontSize: '12px',
                      background: countryValue === country.id ? 'var(--pale-blue)' : 'transparent'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = countryValue === country.id ? 'var(--pale-blue)' : 'transparent'}
                  >
                    <div style={{ fontWeight: '500' }}>
                      {country.countryName} (+{country.countryCode})
                    </div>
                  </div>
                ))}
                <div style={{ height: '1px', background: 'var(--gray-medium)', margin: '4px 0' }}></div>
              </div>
            )}

            {/* All countries */}
            <div>
              {!countrySearch && (
                <div style={{
                  padding: '8px 12px',
                  background: 'var(--gray-light)',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  color: 'var(--gray-dark)'
                }}>
                  All Countries
                </div>
              )}
              {filteredCountries.map((country) => (
                <div
                  key={country.id}
                  onClick={() => {
                    onCountryChange(country.id);
                    setShowCountryDropdown(false);
                    setCountrySearch('');
                  }}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    borderBottom: '1px solid var(--gray-light)',
                    fontSize: '12px',
                    background: countryValue === country.id ? 'var(--pale-blue)' : 'transparent'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = countryValue === country.id ? 'var(--pale-blue)' : 'transparent'}
                >
                  <div style={{ fontWeight: '500' }}>
                    {country.countryName} (+{country.countryCode})
                  </div>
                </div>
              ))}
              {filteredCountries.length === 0 && countrySearch && (
                <div style={{
                  padding: '12px',
                  textAlign: 'center',
                  color: 'var(--gray-dark)',
                  fontSize: '12px'
                }}>
                  No countries found matching "{countrySearch}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Phone Number Input */}
      <input
        type="tel"
        value={phoneValue}
        onChange={(e) => onPhoneChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        style={{
          flex: 1,
          padding: '10px 12px',
          border: '2px solid var(--gray-medium)',
          borderLeft: '1px solid var(--gray-medium)',
          borderRadius: '0 6px 6px 0',
          fontSize: '14px',
          background: disabled ? 'var(--gray-light)' : 'var(--white)'
        }}
      />
    </div>
  );
};

export default CountryPhoneInput;
